// src/shared/filename.js
//
// One naming scheme for every document this app hands out:
//
//     Company_Agreement Title_YYYY-MM-DD
//
// Five places used to name files and they had all drifted apart. The PDF and
// Word downloads were "<Company> - DHIT Agreement", which named neither the
// agreement nor the date — a folder of them was indistinguishable, and re-
// downloading overwrote the previous one. The Google Doc export was the only
// one carrying all three parts. This is that scheme, applied everywhere.
//
// The date is normalised to ISO so a directory listing sorts chronologically,
// whichever way the date reached us.
//
// CommonJS + dependency-free like the rest of src/shared — the Node server
// require()s it and the CRA client imports it.
const { isoDate: sharedIsoDate } = require('./dates');

// Windows, macOS and Drive between them reject or mangle these. Also strip
// control characters, collapse whitespace runs, and drop trailing dots and
// spaces, which Windows silently deletes from a filename.
function safePart(value) {
  return String(value == null ? '' : value)
    .replace(/[/\\?%*:|"<>]/g, '-')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.\s]+$/, '');
}

// "August 24, 2026", "2026-08-24" and a Date object all come out as
// "2026-08-24". Anything unparseable is kept as written rather than dropped: a
// hand-typed date still belongs in the name, and silently omitting it would make
// two agreements for the same client collide.
function isoDate(value) {
  if (!value) return '';
  return safePart(sharedIsoDate(value));
}

// `suffix` marks a variant ("Signed", "Client Signed") and is appended in
// parentheses so the three-part structure stays readable. Returns the bare name;
// callers add the extension, since a Google Doc has none.
function agreementFileName({ customer, title, date, suffix } = {}) {
  const parts = [safePart(customer) || 'Agreement', safePart(title), isoDate(date)].filter(Boolean);
  const base = parts.join('_');
  const tag = safePart(suffix);
  return tag ? `${base} (${tag})` : base;
}

module.exports = { agreementFileName, isoDate, safePart };
